<template>
  <teleport v-if="hasMounted" to="#portal-target">
    <transition name="fade-in-only">
      <div v-if="pendingSequence" class="chord-hint">
        <kbd class="chord-hint__prefix">{{ pendingSequence.prefix }}</kbd>
        <div class="chord-hint__candidates">
          <div v-for="candidate in candidates" :key="candidate.combo" class="chord-hint__row">
            <kbd class="chord-hint__key">{{ candidate.key }}</kbd>
            <span class="chord-hint__description">{{ candidate.description }}</span>
          </div>
          <p v-if="pendingSequence.hint" class="chord-hint__footnote">{{ pendingSequence.hint }}</p>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script setup lang="ts">
import { pendingSequence } from '@lib/shortcuts';
import { useMounted } from '@lib/use-mounted';
import { computed } from 'vue';

const hasMounted = useMounted();

const candidates = computed(() =>
  [...(pendingSequence.value?.candidates ?? [])]
    .filter((candidate) => candidate.description)
    .sort((a, b) => a.key.localeCompare(b.key))
);
</script>

<style scoped lang="scss">
@import '@styles/variables';

.chord-hint {
  position: fixed;
  bottom: 2.4rem;
  left: 50%;
  transform: translateX(-50%);
  z-index: $zi-overlay;
  display: flex;
  align-items: flex-start;
  gap: 1.2rem;
  padding: 1.2rem 1.6rem;
  border-radius: 0.8rem;
  background: rgba($c-dark, 0.92);
  color: white;
  // Purely informative — must never trap clicks meant for the page below.
  pointer-events: none;

  &__prefix,
  &__key {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 2rem;
    padding: 0.2rem 0.5rem;
    border: 1px solid rgba(white, 0.3);
    border-radius: 0.4rem;
    background: rgba(white, 0.1);
    font-family: inherit;
    font-size: 1.2rem;
    line-height: 1.6rem;
  }

  &__candidates {
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
  }

  &__row {
    display: flex;
    align-items: center;
    gap: 0.8rem;
  }

  &__description {
    font-size: 1.3rem;
  }

  &__footnote {
    margin: 0.4rem 0 0;
    color: rgba(white, 0.6);
    font-size: 1.2rem;
  }
}
</style>
